var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState } from "react";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { searchProductNumbers } from "../api/productNumberService";
import { DEFAULT_PAGE_SIZE } from "../utils/constants";
import { toSortByParam } from "./useTableSort";
import { isForbiddenError } from "../utils/httpErrors";
/**
 * One page of the product number list.
 *
 * @param appliedFilters filters the search sends
 * @param pageSize page size
 * @param sort header sort
 * @param enabled false while the filters are still being resolved — nothing is
 *   searched and `isWaitingForFilters` is true
 */
export var useProductNumberData = function (appliedFilters, pageSize, sort, enabled) {
    var _a;
    if (pageSize === void 0) { pageSize = DEFAULT_PAGE_SIZE; }
    if (enabled === void 0) { enabled = true; }
    var ky = useOkapiKy();
    var _b = useState(0), pageOffset = _b[0], setPageOffset = _b[1];
    var _c = useState(0), totalRecords = _c[0], setTotalRecords = _c[1];
    var _d = useQuery([
        "productNumbers",
        appliedFilters,
        pageOffset,
        pageSize,
        sort === null || sort === void 0 ? void 0 : sort.column,
        sort === null || sort === void 0 ? void 0 : sort.direction,
    ], function () {
        return searchProductNumbers(ky, __assign(__assign({}, appliedFilters), { offset: pageOffset, limit: pageSize, sortBy: sort ? toSortByParam(sort) : undefined }));
    }, {
        keepPreviousData: true,
        enabled: enabled,
        // One retry only: the search runs over the whole registry, so the default three retries
        // would keep the list "loading" for minutes before the user learns it failed. A 403 (e.g. -92,
        // a filter refused for the public-only view, DB-837) does not change on retry.
        retry: function (failureCount, error) {
            return !isForbiddenError(error) && failureCount < 1;
        },
        onSuccess: function (responseData) {
            var _a;
            setTotalRecords((_a = responseData.totalRecords) !== null && _a !== void 0 ? _a : 0);
        },
    }), data = _d.data, isLoading = _d.isLoading, isFetching = _d.isFetching, isError = _d.isError, error = _d.error, refetch = _d.refetch, isFetched = _d.isFetched;
    var productNumbersData = (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [];
    var fetchMoreData = function (offset) {
        setPageOffset(offset);
    };
    var resetPageOffset = function () { return setPageOffset(0); };
    return {
        productNumbersData: productNumbersData,
        totalRecords: totalRecords,
        isLoading: isLoading,
        /** true while the search waits for its filters (`enabled` false) — no request is running */
        isWaitingForFilters: !enabled,
        isFetching: isFetching,
        isError: isError,
        error: error,
        isFetched: isFetched,
        refetch: refetch,
        fetchMoreData: fetchMoreData,
        resetPageOffset: resetPageOffset,
        pageOffset: pageOffset,
        pageSize: pageSize,
    };
};
